import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PageHeader } from "../components/PageHeader";
import { Reveal, Overline } from "../components/motion";
import { ROADMAP, PHASES, CAPITAL_STRATEGY, IMAGES } from "../data";

export default function RoadmapPage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <>
      <PageHeader
        overline="04 — The Roadmap"
        lines={["From ₹190 Cr", "to ₹1,000 Cr."]}
        subtitle="A diversified Green Finance portfolio targeted for March 2028 or earlier — diversification is deliberate, so no single customer, OEM or asset class defines the platform."
      />

      {/* Allocation + image */}
      <section ref={ref} className="py-20 md:py-28 bg-bg overflow-hidden" data-testid="roadmap-allocation">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <div>
              <Reveal><Overline>Roadmap to ₹1,000 Crore Green Finance AUM</Overline></Reveal>
              <div className="mt-10 space-y-6">
                {ROADMAP.map((r, i) => (
                  <Reveal key={r.label} delay={i * 0.05}>
                    <div className="group" data-testid={`roadmap-bar-${i}`}>
                      <div className="flex items-baseline justify-between mb-2">
                        <span className="font-heading text-sm font-medium text-ink/80">{r.label}</span>
                        <span className="font-heading text-sm font-bold text-ink">{r.amount}<span className="text-ink/40 ml-2 text-xs">{r.pct}%</span></span>
                      </div>
                      <div className="h-2 w-full bg-ink/8 overflow-hidden">
                        <motion.div initial={{ width: 0 }} whileInView={{ width: `${r.pct * 2}%` }} viewport={{ once: true }} transition={{ duration: 1.1, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] }} className="h-full bg-brand-green group-hover:bg-white transition-colors duration-500" />
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <Reveal delay={0.15}>
              <div className="relative group h-[420px] lg:h-[540px] overflow-hidden border border-ink/10">
                <motion.img style={{ y: imgY }} src={IMAGES.evFleet} alt="India EV bus fleet" className="img-grayscale absolute inset-0 w-full h-[130%] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-green via-brand-green/55 to-brand-green/10" />
                <div className="absolute bottom-0 left-0 p-8">
                  <div className="font-display text-white text-7xl lg:text-8xl leading-none">₹1,000<span className="text-white/70">Cr</span></div>
                  <div className="mt-3 overline text-white/85">Green Finance AUM · March 2028 or earlier</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Phases */}
      <section className="py-20 md:py-28 bg-surface border-t border-ink/10" data-testid="roadmap-phases">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
          <Reveal><Overline>How ₹1,000 Crore becomes achievable</Overline></Reveal>
          <div className="mt-12 border-t border-ink/15">
            {PHASES.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05}>
                <div className="group grid md:grid-cols-12 gap-6 py-10 border-b border-ink/12 hover:bg-bg transition-colors duration-500 px-2 md:px-4">
                  <div className="md:col-span-2"><span className="font-display text-6xl md:text-7xl text-ink/25 group-hover:text-brand-green transition-colors duration-500 leading-none">{p.id}</span></div>
                  <div className="md:col-span-4"><h3 className="font-heading font-extrabold text-ink text-2xl md:text-3xl tracking-tight">{p.phase}</h3><div className="mt-2 overline text-brand-green">{p.period}</div></div>
                  <div className="md:col-span-6 flex items-center"><p className="font-body text-ink/60 leading-relaxed">{p.desc}</p></div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}><p className="mt-8 font-body text-xs text-ink/35 max-w-2xl">Scale is subject to credit quality, capital availability, regulatory readiness and portfolio performance.</p></Reveal>
        </div>
      </section>

      {/* Capital strategy */}
      <section className="py-20 md:py-28 bg-bg border-t border-ink/10" data-testid="roadmap-capital">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-20">
          <Reveal><Overline>Capital Strategy — funding the AUM</Overline></Reveal>
          <Reveal delay={0.05}><h2 className="mt-6 font-heading font-bold text-ink text-2xl md:text-3xl max-w-2xl">Every ₹1 of AUM growth has an identified funding pathway, risk framework and portfolio-level economics.</h2></Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
            {CAPITAL_STRATEGY.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.05}>
                <div className="bg-bg p-8 h-full">
                  <div className="w-8 h-px bg-brand-green" />
                  <h3 className="mt-5 font-heading font-bold text-ink text-lg leading-snug">{c.title}</h3>
                  <p className="mt-3 font-body text-sm text-ink/55 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 inline-flex items-center gap-3 border border-ink/15 px-6 py-4">
              <span className="overline text-brand-green">Risk discipline</span>
              <span className="font-body text-sm text-ink/70">Limits • concentration controls • credit governance</span>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
